import { useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

const BANNER_AD_ID   = 'ca-app-pub-4294867709728732/8653744234';
const REWARDED_AD_ID = 'ca-app-pub-4294867709728732/8692977534';

const ACCESS_DURATION_MS   = 30 * 60 * 1000; // 30 minutes
const FINANCIAL_UNLOCK_KEY = 'calc360_financial_unlock_ts';

const MAX_RETRIES    = 4;
const RETRY_BASE_MS  = 2000; // 2s → 4s → 8s → 16s

// ✅ PWA-Safe Check
const isCapacitor = () => Capacitor.isNativePlatform();

const lsGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const lsSet = (key, val) => { try { localStorage.setItem(key, val); } catch { } };
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

export const isFinancialUnlocked = () => {
  const ts = parseInt(lsGet(FINANCIAL_UNLOCK_KEY) || '0', 10);
  return ts > 0 && Date.now() - ts < ACCESS_DURATION_MS;
};

export const financialMinutesLeft = () => {
  const ts = parseInt(lsGet(FINANCIAL_UNLOCK_KEY) || '0', 10);
  if (!ts) return 0;
  const msLeft = ACCESS_DURATION_MS - (Date.now() - ts);
  return msLeft > 0 ? Math.ceil(msLeft / 60000) : 0;
};

export const useAdMob = ({ adsRemoved }) => {
  const [rewardedReady, setRewardedReady] = useState(false);
  // ✅ Web users start with this unlocked automatically
  const [financialUnlocked, setFinancialUnlocked] = useState(isCapacitor() ? isFinancialUnlocked() : true);
  const listenersRef = useRef([]);

  // Poll every 30s to expire the financial unlock
  useEffect(() => {
    if (!isCapacitor()) return; // Skip polling on web
    const interval = setInterval(() => {
      setFinancialUnlocked(isFinancialUnlocked());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Loads the rewarded ad with exponential-backoff retry on failure.
  const loadRewardedAd = async (AdMob, RewardAdPluginEvents, attempt = 0) => {
    try {
      setRewardedReady(false);

      const loadedHandle = await AdMob.addListener(
        RewardAdPluginEvents.Loaded,
        () => {
          setRewardedReady(true);
          loadedHandle.remove();
        }
      );

      // FailedToLoad: retry with exponential backoff up to MAX_RETRIES
      const failedHandle = await AdMob.addListener(
        RewardAdPluginEvents.FailedToLoad,
        async () => {
          failedHandle.remove();
          loadedHandle.remove();
          if (attempt < MAX_RETRIES) {
            const delay = RETRY_BASE_MS * Math.pow(2, attempt); // 2s, 4s, 8s, 16s
            console.warn(`[AdMob] Load failed, retrying in ${delay / 1000}s (attempt ${attempt + 1})`);
            await sleep(delay);
            await loadRewardedAd(AdMob, RewardAdPluginEvents, attempt + 1);
          } else {
            console.warn('[AdMob] Max retries reached, giving up.');
            setRewardedReady(false);
          }
        }
      );

      await AdMob.prepareRewardVideoAd({
        adId: REWARDED_AD_ID,
        isTesting: false, 
      });
    } catch (err) {
      console.warn('[AdMob] prepareRewardVideoAd error:', err);
      setRewardedReady(false);
    }
  };

  // Initialize banner + first rewarded ad load
  useEffect(() => {
    if (!isCapacitor() || adsRemoved) return;
    const init = async () => {
      try {
        const { AdMob, BannerAdSize, BannerAdPosition, RewardAdPluginEvents } =
          await import('@capacitor-community/admob');

        await AdMob.initialize({ testingDevices: [] });

        await AdMob.showBanner({
          adId: BANNER_AD_ID,
          adSize: BannerAdSize.BANNER,
          position: BannerAdPosition.BOTTOM_CENTER,
          margin: 0,
          isTesting: false,
        });

        await loadRewardedAd(AdMob, RewardAdPluginEvents);

      } catch (err) {
        console.warn('[AdMob] init failed:', err);
      }
    };
    init();
    return () => {
      listenersRef.current.forEach(l => l?.remove());
      listenersRef.current = [];
      if (!isCapacitor()) return;
      import('@capacitor-community/admob').then(({ AdMob }) => {
        AdMob.hideBanner().catch(() => {});
        AdMob.removeAllListeners().catch(() => {});
      });
    };
  }, [adsRemoved]);

  // Hide/remove banner immediately when ads are purchased
  useEffect(() => {
    if (!adsRemoved || !isCapacitor()) return;
    import('@capacitor-community/admob').then(({ AdMob }) => {
      AdMob.hideBanner().catch(() => {});
      AdMob.removeBanner().catch(() => {});
    });
  }, [adsRemoved]);

  const showRewardedAd = useCallback(async ({ onGranted, onDenied }) => {
    // ✅ Auto-grant access if on web, if ads removed, or if already unlocked
    if (!isCapacitor() || adsRemoved || isFinancialUnlocked()) {
      onGranted();
      return;
    }

    if (!rewardedReady) {
      onDenied?.('Ad not ready. Please try again in a moment.');
      return;
    }

    try {
      const { AdMob, RewardAdPluginEvents } = await import('@capacitor-community/admob');
      let rewarded = false;
      setRewardedReady(false); // not ready while ad is on screen

      const rewardHandle = await AdMob.addListener(
        RewardAdPluginEvents.Rewarded, // fires while ad is still showing
        () => {
          rewarded = true;
          lsSet(FINANCIAL_UNLOCK_KEY, String(Date.now()));
          setFinancialUnlocked(true);
          rewardHandle.remove();

          // Preload the next ad immediately
          loadRewardedAd(AdMob, RewardAdPluginEvents);
        }
      );

      const dismissHandle = await AdMob.addListener(
        RewardAdPluginEvents.Dismissed, // fires when ad closes
        async () => {
          dismissHandle.remove();
          if (rewarded) {
            onGranted();
          } else {
            loadRewardedAd(AdMob, RewardAdPluginEvents);
            onDenied?.('Watch the full ad to unlock Financial Tools.');
          }
        }
      );

      await AdMob.showRewardVideoAd();

    } catch (err) {
      console.warn('[AdMob] showRewardVideoAd error:', err);
      onDenied?.('Ad failed. Please try again.');
    }
  }, [adsRemoved, rewardedReady]);

  // ✅ Always return financialUnlocked as true on the web
  return { 
    showRewardedAd, 
    financialUnlocked: !isCapacitor() || financialUnlocked, 
    rewardedReady 
  };
};